import { formats, getCell, withCellStyles } from '../../utils';
import type { ExportExcelOverview, OverviewTotal } from './interfaces';
import { PortfolioPerformance } from 'src/app/domain/pp/dto/PortfolioPerformance';
import { CellStyle } from 'xlsx-js-style/types';
import { Row, Rows } from '../../interfaces';
import { intToExcelCol } from 'excel-column-name';
import {
  boldGrayBgCenter,
  boldLightGrayBgCenter,
  centeredBoldGrayForHeaders,
  zeroOrNullToEmptyString,
} from 'src/app/util/excelStyles';

// eslint-disable-next-line
function zeroToUndefined(v: number) {
  if (v) {
    return v;
  }
  return undefined;
}

/**
 * Get data mapping.
 *
 * @param p PortfolioPerformance to map.
 */
const getNewMapping = (p: PortfolioPerformance): object => {
  const body = {
    Company: p.name,
    Country: p.country,
    Industry: p.industry,
    Group: p.companyGroup,
    'Initial Inv. Date': getCell({ v: p.dateOfInitialInvestment, z: formats.date, t: 'd' }),
    Investment: getCell({ v: zeroToUndefined(p.cost), z: formats.thousandsSeparator, t: 'n' }),
    'Fair value': getCell({ v: zeroToUndefined(p.fairValue), z: formats.thousandsSeparator, t: 'n' }),
    'Gain/Loss': getCell({
      f: prepareGainLossForRows(constHeaders),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Holding %': getCell({ v: zeroToUndefined(p.holdingPercentage), z: formats.percentsNoSpace, t: 'n' }),
    MOIC: getCell({
      f: prepareMoicForRows(constHeaders),
      z: formats.multiplier,
      t: 'n',
    }),
    IRR: getCell({ v: zeroToUndefined(p.calculatedGrossIrr), z: formats.percentsNoSpace, t: 'n' }),
    'Total Raised': getCell({ v: zeroToUndefined(p.totalInvestmentInTheCompany), z: formats.thousandsSeparator, t: 'n' }),
    Valuation: getCell({
      v: zeroToUndefined(p.valuationRoundValue),
      z: formats.thousandsSeparator, t: 'n',
    }),
    'Last round name': p.valuationRoundName,
    'Last round date': p.valuationRoundDate,
    'Current company value': p.valuationRoundValue,
    'Total commitment': getCell({
      v: zeroOrNullToEmptyString(p.totalCommitment),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Unfunded commitment': getCell({
      f: prepareDiffForUnfunded(constHeaders),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
  };
  currentRowIndex++;
  return body;
};


const getTotalMapping = (total: OverviewTotal, headers: string[], startIndex: number): any => {
  return {
    Company: total.name === 'Total sold' ? 'Total realized' : total.name,
    Investment: getCell({
      f: prepareSumForTotal('Investment', startIndex, headers),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value': getCell({
      f: prepareSumForTotal('Fair value', startIndex, headers),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/Loss': getCell({
      f: prepareSumForTotal('Gain/Loss', startIndex, headers),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    MOIC: getCell({ f: prepareMoicForRows(headers), z: formats.multiplier, t: 'n' }),
    IRR: getCell({
      v: zeroOrNullToEmptyString(total.irr),
      z: formats.percentsNoSpace,
      t: 'n',
    }),
    'Total Raised': getCell({
      f: prepareSumForTotal('Total Raised', startIndex, headers),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    Valuation: getCell({
      f: prepareSumForTotal('Valuation', startIndex, headers),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    Industry: '',
    Country: '',
    'Initial Inv. Date': '',
    'Holding %': '',
    Group: '',
    'Last round name': '',
    'Last round date': '',
    'Total commitment': getCell({
      f: prepareSumForTotal('Total commitment', startIndex, headers),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Unfunded commitment': getCell({
      f: prepareSumForTotal('Unfunded commitment', startIndex, headers),
      z: formats.thousandsSeparator,
      t: 'n',
    }),

  };
};

const getTotalMappingForGrandTotal = (total: OverviewTotal, headers: string[]): any => {
  return {
    Company: total.name,
    Investment: getCell({
      f: prepareSumForGrandTotal('Investment', headers),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value': getCell({
      f: prepareSumForGrandTotal('Fair value', headers),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/Loss': getCell({
      f: prepareSumForGrandTotal('Gain/Loss', headers),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    MOIC: getCell({ f: prepareMoicForRows(headers), z: formats.multiplier, t: 'n' }),
    IRR: getCell({ v: zeroToUndefined(total.irr), z: formats.percentsNoSpace, t: 'n' }),
    'Total Raised': getCell({
      f: prepareSumForGrandTotal('Total Raised', headers),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    Valuation: getCell({
      f: prepareSumForGrandTotal('Valuation', headers),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    Industry: '',
    Country: '',
    'Initial Inv. Date': '',
    'Holding %': '',
    Group: '',
    'Last round name': '',
    'Last round date': '',
    'Total commitment': getCell({
      f: prepareSumForGrandTotal('Total commitment', headers),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Unfunded commitment': getCell({
      f: prepareSumForGrandTotal('Unfunded commitment', headers),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
  };
};

/**
 * Count sums.
 *
 * @param active PortfolioPerformance to sum over.
 * @param sold PortfolioPerformance to sum over.
 * @returns OverviewTotal totals array.
 */
export const getTotals = (exportExcelOverview: ExportExcelOverview): OverviewTotal[] => {
  const [active, sold, activeIrr, soldIrr, grandIrr] = [exportExcelOverview.active, exportExcelOverview.sold,
    exportExcelOverview.totalActiveIrr, exportExcelOverview.totalSoldIrr, exportExcelOverview.grandTotalIrr];
  function getNewTotal(status: 'Total active' | 'Total sold' | 'Grand total'): OverviewTotal {
    return {
      name: status,
      cost: 0,
      fairValue: 0,
      multiple: 0,
      irr: 0,
      totalInvestmentInTheCompany: 0,
      valuation: 0,
    };
  }

  const getSum = (t: OverviewTotal, p: PortfolioPerformance): OverviewTotal => {
    t.cost += p.cost;
    t.fairValue += p.fairValue;
    t.totalInvestmentInTheCompany += p.totalInvestmentInTheCompany;
    t.valuation += p.valuationRoundValue;
    if (t.cost) {
      t.multiple = t.fairValue / t.cost;
    }
    // eslint-disable-next-line no-nested-ternary
    t.irr = t.name.includes('active') ? activeIrr : (t.name.includes('sold') ? soldIrr : grandIrr);
    return t;
  };

  const totalActive = active.reduce(getSum, getNewTotal('Total active'));
  const totalSold = sold.reduce(getSum, getNewTotal('Total sold'));
  const grandTotal = [...active, ...sold].reduce(getSum, getNewTotal('Grand total'));

  return [totalActive, totalSold, grandTotal];
};


/**
 * Prepares rows for defined rounds.
 *
 * @param ppList PortfolioPerformance list.
 * @param headers Table headers.
 * @returns prepared rows for defined rounds.
 */
export const getManyRows = (ppList: PortfolioPerformance[], headers: string[]): Rows => {
  const rows: Rows = [];
  const mappedToHeaders = ppList.map((d) => getNewMapping(d));
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of headers) {
      row.push(withCellStyles(mapped[header], centered));
    }
    rows.push(row);
  }
  return rows;
};

/**
 * Prepares row for defined round.
 *
 * @param total OverviewTotal.
 * @param headers Table headers.
 * @returns prepared row for defined round.
 */
export const getOneRow = (total: OverviewTotal, headers: string[]): Row => {
  // eslint-disable-next-line no-nested-ternary
  const startIndex = total.name === 'Total active' ? activeStarts : total.name === 'Total sold' ? soldStarts : null;
  const mapped = getTotalMapping(total, headers, startIndex);
  const row: Row = [];
  for (const header of headers) {
    row.push(withCellStyles(mapped[header], boldLightGrayBgCenter));
  }
  return row;
};

export const getOneRowForGrandTotal = (total: OverviewTotal, headers: string[]): Row => {
  const mapped = getTotalMappingForGrandTotal(total, headers);
  const row: Row = [];
  for (const header of headers) {
    row.push(withCellStyles(mapped[header], boldGrayBgCenter));
  }
  return row;
};

/**
 * Prepare data for excel export by adding styles, etc.
 *
 * @param exportExcelOverview export data for all companies.
 * @param headers Headers of the table.
 * @returns complete data for Excel - rows.
 */
export const getPreparedData = (exportExcelOverview: ExportExcelOverview, headers: string[]): Rows => {
  const [active, sold] = [exportExcelOverview.active, exportExcelOverview.sold];
  const [totalActive, totalSold, grandTotal] = getTotals(exportExcelOverview);

  const newData: Rows = [];
  const headerRow = [];
  headers.forEach((header) => headerRow.push(withCellStyles(header, centeredBoldGrayForHeaders)));
  newData.push(headerRow);
  constHeaders = headers;
  activeLen = active.length;
  soldLen = sold.length;
  if (active.length) {
    getActiveOrSoldRows(newData, 'Active', active, totalActive, headers);
  }
  if (sold.length) {
    getActiveOrSoldRows(newData, 'Sold', sold, totalSold, headers);
  }
  if (sold.length && active.length) {
    newData.push(getOneRowForGrandTotal(grandTotal, headers));
  }
  refresh();
  return newData;
};

export const getActiveOrSoldRows = (newData, status: string, pps: PortfolioPerformance[], totals, headers: string[]): void => {
  newData.push([getCell({ v: status === 'Sold' ? 'Realized' : status, s: { font: { bold: true } } })]);
  currentRowIndex++;
  if (status === 'Active') {
    activeStarts = currentRowIndex;
    activeTotalIndex = currentRowIndex + 1 + pps.length;
  } else {
    soldStarts = currentRowIndex;
    soldTotalIndex = currentRowIndex + pps.length + 1;
  }
  const dataRows = getManyRows(pps, headers);
  newData.push(...dataRows);
  if (status === 'Active' ? activeLen : soldLen) {
    newData.push(getOneRow(totals, headers));
    currentRowIndex++;
  }
};

export const prepareGainLossForRows = (headers: string[]): string => {
  const fmvColumn = headers.findIndex((header) => header === 'Fair value');
  const invColumn = headers.findIndex((header) => header === 'Investment');
  const fmvLetter = intToExcelCol(fmvColumn + 1);
  const invLetter = intToExcelCol(invColumn + 1);
  return `=${fmvLetter}${currentRowIndex}-${invLetter}${currentRowIndex}`;
};

export const prepareMoicForRows = (headers: string[]): string => {
  const fmvColumn = headers.findIndex((header) => header === 'Fair value');
  const invColumn = headers.findIndex((header) => header === 'Investment');
  const fmvLetter = intToExcelCol(fmvColumn + 1);
  const invLetter = intToExcelCol(invColumn + 1);
  return `=IFERROR(${fmvLetter}${currentRowIndex}/${invLetter}${currentRowIndex},0)`;
};

export const prepareSumForTotal = (field: string, startIndex: number, headers: string[]): string => {
  const currentColumnIndex = headers.findIndex((header) => header === field);
  const startLetter = intToExcelCol(currentColumnIndex + 1);
  return `=SUM(${startLetter}${startIndex}:${startLetter}${currentRowIndex - 1})`;
};

export const prepareSumForGrandTotal = (field: string, headers: string[]): string => {
  const currentColumnIndex = headers.findIndex((header) => header === field);
  const startLetter = intToExcelCol(currentColumnIndex + 1);
  return `=SUM(${startLetter}${activeTotalIndex - 1},${startLetter}${soldTotalIndex - 1})`;
};

/**
 * @param headers
 * @returns formula for Unfunded commitment
 */
export const prepareDiffForUnfunded = (headers: string[]): string => {
  const totalComIndex = headers.findIndex((header) => header === 'Total commitment');
  const inv = headers.findIndex((header) => header === 'Investment');
  const invLetter = intToExcelCol(inv + 1);
  const totalComLetter = intToExcelCol(totalComIndex + 1);
  return `IF(${totalComLetter}${currentRowIndex}<>"",${totalComLetter}${currentRowIndex}-${invLetter}${currentRowIndex},"")`;
};


/**
 * Refresh function called when excel book is ready and needs to refresh data.
 */
export const refresh = (): void => {
  // start value of row index after headers and status rows
  currentRowIndex = 2;
  activeTotalIndex = undefined;
  activeStarts = undefined;
  soldStarts = undefined;
  soldStarts = undefined;
  activeLen = 0;
  soldLen = 0;
  constHeaders = [];
};

let currentRowIndex = 2;
let activeTotalIndex: number;
let activeStarts: number;
let soldStarts: number;
let soldTotalIndex: number;
let activeLen = 0;
let soldLen = 0;
// this headers needs to call headers without changing func signature
let constHeaders: string[] = [];


const centered: CellStyle = {
  alignment: { horizontal: 'center' },
};


